<script setup>
import { ref, inject, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";

import HomeIcon from "../../../assets/vue-icons/home-icon.vue";
import SearchIcon from "../../../assets/vue-icons/search-sidebar-icon.vue";
import ProjectIcon from "../../../assets/vue-icons/all-project-nav-icon.vue";
import DocIcon from "../../../assets/vue-icons/document-icon.vue";
import ProfileMenu from "../ui/ProfileMenu.vue";

const props = defineProps({
  activePanel: { type: String, default: null }, // 'projects' | 'docs' | null
});
const emit = defineEmits(["create", "toggle-panel"]);

const router = useRouter();
const route = useRoute();
const $auth0 = inject("$auth0");

const userInitials = ref("?");
const userName = ref("");
const userEmail = ref("");
const showProfileMenu = ref(false);

onMounted(async () => {
  const user = await $auth0.getUser();
  const name = user.profile.name ?? user.profile.email ?? "";
  const email = user.profile.email ?? "";
  const parts = name.trim().split(" ");
  userInitials.value = parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : name.slice(0, 2).toUpperCase();
  userName.value = name;
  userEmail.value = email;
});

// Items that navigate directly (no panel)
const directNavItems = [
  { name: "v2-search",   label: "Search",   icon: SearchIcon,  svgFallback: null },
  { name: "v2-activity", label: "Activity",  icon: null,        svgFallback: "activity" },
];

// Items that navigate + toggle a secondary panel
const panelNavItems = [
  { name: "v2-projects", panel: "projects", label: "Projects", icon: ProjectIcon },
  { name: "v2-docs",     panel: "docs",     label: "Docs",     icon: DocIcon },
];

function isRouteActive(name) {
  return route.name === name;
}

// Viewing an individual project (v2-project) counts as being on the "Projects" panel's route too
const ROUTE_TO_PANEL = {
  "v2-projects": "projects",
  "v2-project":  "projects",
  "v2-docs":     "docs",
};

function isPanelRouteActive(panel) {
  return ROUTE_TO_PANEL[route.name] === panel;
}

function navigate(name) {
  if (router.hasRoute(name)) router.push({ name });
}

// Navigate to the page AND toggle the panel
function handlePanelItem(item) {
  navigate(item.name);
  emit("toggle-panel", item.panel);
}
</script>

<template>
  <nav class="flex flex-col items-center w-20 min-w-[80px] h-screen  border-slate-200  dark:border-slate-700 bg-white dark:bg-slate-800 py-4">

    <!-- Logo -->
    <div class="w-14 flex items-center justify-center overflow-hidden mb-5">
      <svg width="29" height="32" viewBox="0 0 29 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clip-path="url(#clip0_3826_13212)">
          <path d="M12.878 27.3132C11.9368 29.0556 11.1013 30.0926 10.8303 30.0926C10.6309 30.0926 10.4588 29.5305 10.4588 28.834C10.4588 26.2889 11.9529 22.4925 13.7912 18.7859C14.1942 19.5454 14.3866 20.6499 14.3866 22.1552C14.3866 23.5513 13.8269 25.5563 12.878 27.3132ZM19.7164 14.9709C18.272 16.7983 17.505 16.8576 16.8791 16.8576C16.4773 16.8576 15.6711 16.6503 15.0542 16.3424C16.6954 13.2865 18.4461 10.4996 19.483 8.80323C20.4684 7.19125 19.8323 6.27094 18.9269 6C18.6333 6.44341 15.8563 10.5954 13.194 15.6192C12.9824 15.5346 11.462 14.9375 11.462 14.9375C11.462 14.9375 10.534 16.6666 11.7481 17.2752C11.8068 17.3067 11.8961 17.3534 12.0223 17.4162C12.0795 17.4446 12.1446 17.4768 12.2181 17.5127C10.1948 21.5733 8.5 25.8605 8.5 28.834C8.5 31.0211 9.58213 32 10.654 32C11.3444 32 12.7609 31.5346 14.481 28.4144C15.6489 26.299 16.3155 23.9588 16.3155 22.1552C16.3155 21.1411 16.0561 19.4782 15.6782 18.5755C16.024 18.6806 16.5339 18.7033 16.6212 18.707C17.9015 18.7676 19.0064 18.5244 20.2587 16.9281C20.843 16.0051 20.2487 14.8886 19.7164 14.9709Z" fill="#F04E23"/>
        </g>
        <defs>
          <clipPath id="clip0_3826_13212">
            <rect width="29" height="32" fill="white"/>
          </clipPath>
        </defs>
      </svg>
    </div>

    <!-- Create button -->
    <button
      class="flex items-center justify-center w-14 h-14 rounded-[14px] bg-slate-700 dark:bg-slate-600 hover:opacity-85 transition-opacity mb-6 border-0 cursor-pointer"
      @click="emit('create')"
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path d="M12 5v14M5 12h14" stroke="white" stroke-width="2.5" stroke-linecap="round" />
      </svg>
    </button>

    <!-- Search + Activity (direct nav) -->
    <div class="flex flex-col items-center gap-1 w-full px-2">
      <!-- Search -->
      <button
        :class="['flex flex-col items-center gap-1 w-full py-2 px-1 rounded-[10px] border-0 cursor-pointer transition-colors text-xs font-medium',
          isRouteActive('v2-search')
            ? 'bg-blue-50 dark:bg-blue-950 text-blue-500 dark:text-blue-400'
            : 'bg-transparent text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-700 dark:hover:text-slate-200']"
        @click="navigate('v2-search')"
      >
        <SearchIcon class="w-5 h-5 icon" />
        <span>Search</span>
      </button>

      <!-- Activity — disabled until this section ships -->
      <button
        type="button"
        disabled
        title="Coming soon"
        class="flex flex-col items-center gap-1 w-full py-2 px-1 rounded-[10px] border-0 bg-transparent text-slate-300 dark:text-slate-600 text-xs font-medium cursor-not-allowed"
      >
        <svg class="w-5 h-5" viewBox="0 0 20 20" fill="none">
          <path d="M7.5 13.958C7.5 13.958 7.5 16.0413 10 16.0413C12.5 16.0413 12.5 13.958 12.5 13.958M14.375 8.33301V9.99967L16.0417 13.5413H3.95833L5.625 9.99967V8.33301C5.625 5.91676 7.58375 3.95801 10 3.95801C12.4162 3.95801 14.375 5.91676 14.375 8.33301Z"
            stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        <span>Activity</span>
      </button>
    </div>

    <div class="w-10 h-px bg-slate-200 dark:bg-slate-700 my-3" />

    <!-- Home (direct nav) -->
    <div class="flex flex-col items-center gap-1 w-full px-2">
      <button
        :class="['flex flex-col items-center gap-1 w-full py-2 px-1 rounded-[10px] border-0 cursor-pointer transition-colors text-xs font-medium',
          isRouteActive('v2-home')
            ? 'bg-blue-50 dark:bg-blue-950 text-blue-500 dark:text-blue-400'
            : 'bg-transparent text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-700 dark:hover:text-slate-200']"
        @click="navigate('v2-home')"
      >
        <HomeIcon class="w-5 h-5 icon" />
        <span>Home</span>
      </button>
    </div>

    <!-- <div class="w-10 h-px bg-slate-200 dark:bg-slate-700 my-3" /> -->

    <!-- Projects + Docs (navigate + panel toggle) -->
    <div class="flex flex-col items-center gap-1 w-full">
      <div
        v-for="item in panelNavItems"
        :key="item.panel"
        class="relative w-full px-2"
      >
        <button
          :class="['flex flex-col items-center gap-1 w-full py-2 px-1 rounded-[10px] border-0 cursor-pointer transition-colors text-xs font-medium',
            isPanelRouteActive(item.panel)
              ? activePanel === item.panel
                ? 'bg-blue-50 dark:bg-blue-950 text-blue-500 dark:text-blue-400'
                : 'bg-blue-50/60 dark:bg-blue-950/60 text-blue-400 dark:text-blue-400'
              : 'bg-transparent text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-700 dark:hover:text-slate-200']"
          @click="handlePanelItem(item)"
        >
          <component :is="item.icon" class="w-5 h-5 icon" />
          <span>{{ item.label }}</span>
        </button>

        <!-- Panel-open indicator: tall thick bar on right edge -->
        <div
          v-if="activePanel === item.panel"
          class="absolute right-0 top-1 bottom-1 w-[3px] bg-blue-500 dark:bg-blue-400 rounded-l-full transition-all"
        />
        <!-- Route-active but panel closed: short thin bar -->
        <div
          v-else-if="isPanelRouteActive(item.panel)"
          class="absolute right-0 top-[30%] bottom-[30%] w-[2px] bg-blue-300 dark:bg-blue-600 rounded-l-full transition-all"
        />
      </div>
    </div>

    <!-- Spacer -->
    <div class="flex-1" />

    <!-- Avatar + profile menu -->
    <div class="relative">
      <button
        class="flex items-center justify-center w-9 h-9 rounded-full bg-[#7ba7d4] text-white text-[13px] font-semibold border-0 cursor-pointer hover:opacity-85 transition-opacity select-none"
        @click.stop="showProfileMenu = !showProfileMenu"
      >
        {{ userInitials }}
      </button>
      <ProfileMenu
        v-if="showProfileMenu"
        :name="userName"
        :email="userEmail"
        :initials="userInitials"
        @close="showProfileMenu = false"
      />
    </div>
  </nav>
</template>

<style scoped>
/* Force SVG icon paths to inherit currentColor — cannot be done with Tailwind utilities */
.icon :deep(svg path),
.icon :deep(svg circle),
.icon :deep(svg rect) {
  stroke: currentColor;
}
</style>
